/**
 * CR415: Published Deal Card Config Context
 * 
 * Provides environment-aware published CR412 config to any rendering surface.
 * Wrapping a component tree with this provider makes the published config
 * available for deal card rendering via template overrides.
 */

import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from "react";
import { usePublishedDealCardConfig, type PublishedDealCardConfig, type DealCardPublishedConfigJson } from "@/hooks/usePublishedDealCardConfig";
import type { PublishEnvironment } from "@/hooks/usePromoteDealCardConfig";
import { normalizeComponentsShape } from "@/lib/deal-cards/normalizeComponents";

// ================================================================
// TYPES
// ================================================================

interface PublishedConfigContextValue {
  /** Current environment */
  environment: PublishEnvironment;
  /** Set environment (triggers refetch) */
  setEnvironment: (env: PublishEnvironment) => void;
  /** Published configs keyed by deal type */
  configs: Map<string, PublishedDealCardConfig>;
  /** Loading state */
  loading: boolean;
  /** Fetch config for a specific deal type */
  fetchForDealType: (dealType: string) => Promise<void>;
  /** Get template override for a deal type (ready for bridgeTemplateToV2Spec) */
  getTemplateOverride: (dealType: string) => Record<string, unknown> | undefined;
}

// ================================================================
// CONTEXT
// ================================================================

const PublishedDealCardConfigContext = createContext<PublishedConfigContextValue | null>(null);

// ================================================================
// PROVIDER
// ================================================================

interface PublishedDealCardConfigProviderProps {
  /** Initial environment */
  environment?: PublishEnvironment;
  /** Deal types to prefetch */
  dealTypes?: string[];
  children: ReactNode;
}

const CANONICAL_DEAL_TYPES = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"];

export function PublishedDealCardConfigProvider({
  environment: initialEnv = "production",
  dealTypes = CANONICAL_DEAL_TYPES,
  children,
}: PublishedDealCardConfigProviderProps) {
  const [environment, setEnvironment] = useState<PublishEnvironment>(initialEnv);
  const [configs, setConfigs] = useState<Map<string, PublishedDealCardConfig>>(new Map());
  const [loading, setLoading] = useState(false);
  
  const { fetchPublished: fetchConfig } = usePublishedDealCardConfig();

  const fetchForDealType = useCallback(async (dealType: string) => {
    const result = await fetchConfig(environment, dealType);
    if (result) {
      setConfigs(prev => {
        const next = new Map(prev);
        next.set(dealType, result);
        return next;
      });
    }
  }, [environment, fetchConfig]);

  // Prefetch all deal types on mount / environment change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setConfigs(new Map());

    Promise.all(
      dealTypes.map(async (dt) => {
        const result = await fetchConfig(environment, dt);
        if (!cancelled && result) {
          setConfigs(prev => {
            const next = new Map(prev);
            next.set(dt, result);
            return next;
          });
        }
      })
    ).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [environment, fetchConfig, dealTypes.join(",")]);

  const getTemplateOverride = useCallback((dealType: string) => {
    const cfg = configs.get(dealType);
    if (!cfg?.config_json) return undefined;
    const json = cfg.config_json;
    const de = (json.data_elements ?? {}) as Record<string, unknown>;
    const rawComp = (json.components ?? {}) as Record<string, unknown>;
    // CR421: Normalize components.components nesting to flat canonical shape
    const comp = normalizeComponentsShape(rawComp) ?? rawComp;
    return {
      sections: de.sections,
      labels: de.labels,
      header: de.header,
      extended_sections: de.extended_sections,
      styling: de.styling,
      section_order: de.section_order,
      theme_preset: de.theme_preset,
      surfaceOverrides: de.surfaceOverrides,
      components: comp,
    } as Record<string, unknown>;
  }, [configs]);

  const value = useMemo<PublishedConfigContextValue>(() => ({
    environment,
    setEnvironment,
    configs,
    loading,
    fetchForDealType,
    getTemplateOverride,
  }), [environment, configs, loading, fetchForDealType, getTemplateOverride]);

  return (
    <PublishedDealCardConfigContext.Provider value={value}>
      {children}
    </PublishedDealCardConfigContext.Provider>
  );
}

// ================================================================
// HOOK
// ================================================================

export function usePublishedDealCardConfigContext() {
  return useContext(PublishedDealCardConfigContext);
}
